import React, { useState, useEffect } from 'react'
import styled from 'styled-components'
import theme from '../styles/theme'
import { getUserNotes, createUserNote, lookupMarianaTekUser } from '../services/api'
import { useAuth } from './auth/AuthProvider'

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  padding: 2rem;
`

const ModalContent = styled.div`
  background: #fefacd;
  border-radius: 4px;
  width: 100%;
  max-width: 600px;
  max-height: 80vh;
  overflow-y: auto;
  box-shadow: 0 15px 50px rgba(0, 0, 0, 0.5);
  border: 1px solid #e8e2a0;
`

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 2rem;
  border-bottom: 2px solid #e8e2a0;
  background: #fef9b8;
`

const ModalTitle = styled.h2`
  font-size: 2rem;
  font-weight: 700;
  color: #444;
  margin: 0;
`

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 2.4rem;
  color: #666;
  cursor: pointer;
  padding: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    color: #333;
  }
`

const NotesContainer = styled.div`
  padding: 2rem;
  max-height: 300px;
  overflow-y: auto;
`

const NoteItem = styled.div`
  margin-bottom: 1.5rem;
  padding: 1rem;
  background: #fff9d9;
  border-left: 3px solid #d4a017;
  border-radius: 2px;
`

const NoteText = styled.p`
  color: #333;
  font-size: 1.4rem;
  margin: 0 0 0.5rem 0;
  white-space: pre-wrap;
  word-break: break-word;
`

const NoteMetadata = styled.div`
  font-size: 1.2rem;
  color: #666;
  font-style: italic;
`

const NoNotesMessage = styled.div`
  text-align: center;
  color: #666;
  font-style: italic;
  padding: 2rem;
`

const LoadingMessage = styled.div`
  text-align: center;
  color: #666;
  padding: 2rem;
`

const NewNoteSection = styled.div`
  padding: 2rem;
  border-top: 2px solid #e8e2a0;
  background: #fef9b8;
`

const Label = styled.label`
  display: block;
  font-size: 1.4rem;
  font-weight: 600;
  color: #444;
  margin-bottom: 0.8rem;
`

const TextArea = styled.textarea`
  width: 100%;
  padding: 1rem;
  font-size: 1.4rem;
  border: 1px solid #d4a017;
  border-radius: 4px;
  background: white;
  color: #333;
  min-height: 100px;
  resize: vertical;
  font-family: inherit;

  &:focus {
    outline: none;
    border-color: #b8860b;
  }

  &::placeholder {
    color: #999;
  }
`

const ButtonContainer = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 1rem;
`

const Button = styled.button<{ variant?: 'primary' | 'secondary' }>`
  padding: 1rem 2rem;
  font-size: 1.4rem;
  font-weight: 600;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s;

  ${props => props.variant === 'primary' ? `
    background: #d4a017;
    color: white;
    border: none;

    &:hover:not(:disabled) {
      background: #b8860b;
    }

    &:disabled {
      background: #ccc;
      cursor: not-allowed;
      opacity: 0.6;
    }
  ` : `
    background: transparent;
    color: #444;
    border: 1px solid #d4a017;

    &:hover {
      background: #fff9d9;
    }
  `}
`

const ErrorMessage = styled.div`
  background: #ffe6e6;
  color: #d70036;
  padding: 1rem;
  border-radius: 4px;
  margin-top: 1rem;
  font-size: 1.3rem;
`

const SuccessMessage = styled.div`
  background: #e6ffe6;
  color: #007700;
  padding: 1rem;
  border-radius: 4px;
  margin-top: 1rem;
  font-size: 1.3rem;
`

interface UserNotesModalProps {
  isOpen: boolean
  onClose: () => void
  userData: any
}

const UserNotesModal: React.FC<UserNotesModalProps> = ({ isOpen, onClose, userData }) => {
  const { user: currentUser } = useAuth()
  const [notes, setNotes] = useState<any[]>([])
  const [isLoadingNotes, setIsLoadingNotes] = useState(false)
  const [newNoteText, setNewNoteText] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [message, setMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null)
  const [adminMarianaTekId, setAdminMarianaTekId] = useState<string | null>(null)

  useEffect(() => {
    if (isOpen && userData?.user?.external_id) {
      loadNotes()
    }
  }, [isOpen, userData?.user?.external_id])

  // Fetch admin user's Mariana Tek ID when modal opens
  useEffect(() => {
    const fetchAdminMarianaTekId = async () => {
      if (isOpen && currentUser?.email && !adminMarianaTekId) {
        try {
          console.log('Fetching Mariana Tek ID for admin user:', currentUser.email)
          const adminMTData = await lookupMarianaTekUser(currentUser.email)
          const adminMTId = adminMTData?.data?.[0]?.id
          if (adminMTId) {
            console.log('Admin Mariana Tek ID:', adminMTId)
            setAdminMarianaTekId(adminMTId)
          } else {
            console.warn('Admin user not found in Mariana Tek')
          }
        } catch (err) {
          console.error('Failed to fetch admin Mariana Tek ID:', err)
        }
      }
    }

    fetchAdminMarianaTekId()
  }, [isOpen, currentUser?.email, adminMarianaTekId])

  const loadNotes = async () => {
    if (!userData?.user?.external_id) return

    setIsLoadingNotes(true)
    setMessage(null)
    try {
      const notesData = await getUserNotes(userData.user.external_id)
      setNotes(notesData)
    } catch (err) {
      console.error('Failed to load notes:', err)
      setMessage({
        type: 'error',
        text: err instanceof Error ? err.message : 'Failed to load notes'
      })
      setNotes([])
    } finally {
      setIsLoadingNotes(false)
    }
  }

  const handleSubmitNote = async () => {
    if (!newNoteText.trim() || !userData?.user?.external_id) return

    setIsSubmitting(true)
    setMessage(null)

    try {
      // Use admin's Mariana Tek ID as author, or fallback to the searched user's ID
      const authorId = adminMarianaTekId || userData.user.external_id
      console.log('Creating note with author ID:', authorId, '(admin MT ID:', adminMarianaTekId, ')')

      await createUserNote(userData.user.external_id, authorId, newNoteText.trim())

      setMessage({ type: 'success', text: 'Note created successfully!' })
      setNewNoteText('')

      // Reload notes after a brief delay
      setTimeout(() => {
        loadNotes()
      }, 500)
    } catch (err) {
      console.error('Failed to create note:', err)
      setMessage({
        type: 'error',
        text: err instanceof Error ? err.message : 'Failed to create note'
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleClose = () => {
    if (!isSubmitting) {
      onClose()
      // Reset state after closing
      setTimeout(() => {
        setNewNoteText('')
        setMessage(null)
        setNotes([])
      }, 300)
    }
  }

  const formatDate = (dateString: string) => {
    if (!dateString) return ''
    try {
      return new Date(dateString).toLocaleString()
    } catch {
      return dateString
    }
  }

  if (!isOpen) return null

  return (
    <ModalOverlay onClick={handleClose}>
      <ModalContent onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <ModalTitle>User Notes</ModalTitle>
          <CloseButton onClick={handleClose} disabled={isSubmitting}>×</CloseButton>
        </ModalHeader>

        <NotesContainer>
          {isLoadingNotes ? (
            <LoadingMessage>Loading notes...</LoadingMessage>
          ) : notes.length > 0 ? (
            notes.map((note, index) => (
              <NoteItem key={note.id || index}>
                <NoteText>{note.text}</NoteText>
                <NoteMetadata>
                  {note.author_name && `By: ${note.author_name} • `}
                  {formatDate(note.created_at || note.note_datetime)}
                </NoteMetadata>
              </NoteItem>
            ))
          ) : (
            <NoNotesMessage>No notes found for this user</NoNotesMessage>
          )}
        </NotesContainer>

        <NewNoteSection>
          <Label htmlFor="newNote">Enter new notes here</Label>
          <TextArea
            id="newNote"
            value={newNoteText}
            onChange={(e) => setNewNoteText(e.target.value)}
            placeholder="Type your note here..."
            disabled={isSubmitting}
          />

          <ButtonContainer>
            <Button
              variant="primary"
              onClick={handleSubmitNote}
              disabled={isSubmitting || !newNoteText.trim()}
            >
              {isSubmitting ? 'Saving...' : 'Save Note'}
            </Button>
          </ButtonContainer>

          {message && (
            message.type === 'success' ?
              <SuccessMessage>{message.text}</SuccessMessage> :
              <ErrorMessage>{message.text}</ErrorMessage>
          )}
        </NewNoteSection>
      </ModalContent>
    </ModalOverlay>
  )
}

export default UserNotesModal
